import { makeStyles } from 'tss-react/mui';
import clsx from 'clsx';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Box,
  Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';
import LaunchIcon from '@mui/icons-material/Launch';
import { numberToUSDCurrency } from '../../../../helpers/numberToUSDCurrency';
import CommonButton from '../../../../components/buttons/CommonButton';
import TablePaginationLocalContainer from '../../../../components/Table/TablePaginationLocalContainer';
import TruncatedText from '../../../../components/TruncatedText';
import { ROWS_PER_PAGE_OPTIONS, NETSUITE_LABEL } from '../../../../constants/constants';
import { DraftDeductionField } from '../../../../../src/types';

const useStyles = makeStyles<{ isLongData?: boolean }>()((theme: Theme) => ({
  redLeftBorder: {
    borderLeft: '4px solid #FF8663',
    color: '#FF8663 !important',
  },
  yellowLeftBorder: {
    borderLeft: '4px solid #F4B139',
    color: '#F4B139 !important',
  },
  accordionRoot: {
    backgroundColor: 'transparent',
    borderRadius: '16px',
    // border: `1px solid ${theme.palette.secondary.dark}`,
    marginTop: theme.spacing(2),
    boxShadow: 'none',
    overflow: 'hidden',
    fontFamily: 'Inter',
  },
  accordionSummary: {
    backgroundColor: theme.palette.primary.light,
    minHeight: '56px',
    padding: '0 16px',
    fontFamily: 'Inter',
    '& .MuiTypography-root': {
      fontWeight: 600,
      fontSize: '16px',
      color: theme.palette.common.white,
      fontFamily: 'Inter',
    },
    flexDirection: 'row-reverse',
  },
  accordionDetails: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.primary.light,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3), // reduced gap
    fontFamily: 'Inter',
  },
  tableWrapper: {
    overflow: 'hidden',
    background: theme.palette.primary.light,
    borderRadius: '8px',
    fontFamily: 'Inter',
  },
  tableTitle: {
    fontWeight: 600,
    fontSize: '15px',
    color: theme.palette.common.white,
    padding: theme.spacing(1.5, 2),
    fontFamily: 'Inter',
  },
  tableContainer: {
    background: 'transparent',
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: '8px',
    overflow: 'auto',
    fontSize: '14px',
    lineHeight: '17px',
    fontWeight: 500,
    fontFamily: 'Inter',
    // minHeight: isLongData ? '300px' : 'unset',
    // minHeight removed to prevent extra space
    '&::-webkit-scrollbar': {
      width: '5px',
      height: '5px',
      borderRadius: '20px',
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: 20,
      backgroundColor: theme.palette.secondary.dark,
    },
  },

  tableHeaderCell: {
    background: theme.palette.primary.light,
    '& .MuiTableCell-root': {
      textAlign: 'left',
      color: theme.palette.text.secondary,
      borderBottom: '1px solid #515151',
      fontWeight: 500,
      fontFamily: 'Inter',
      fontSize: '14px',
      height: '35px',
      padding: '16px 12px 16px 8px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  tableBodyCell: {
    '& .MuiTableCell-root': {
      textAlign: 'left',
      color: theme.palette.common.white,
      borderBottom: '1px solid #4A4E5D',
      fontWeight: 400,
      fontFamily: 'Inter',
      fontSize: '14px',
      padding: '16px 12px 16px 8px',
      minWidth: '140px',
      maxWidth: '150px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      wordWrap: 'break-word',
      wordBreak: 'break-word',
    },
  },
  labelText: {
    fontSize: '12px !important',
    fontFamily: 'Inter !important',
    color: '#FFFFFFB3 !important',
    fontWeight: '200 !important',
    paddingTop: '2px !important',
  },
  columnName: {
    minWidth: 320,
    maxWidth: 320,
  },
  columnAmount: {
    minWidth: 210,
    maxWidth: 210,

  },
  columnAccount: {
    minWidth: 320,
    maxWidth: 320,
    whiteSpace: 'normal',
    overflow: 'visible',
    textOverflow: 'clip',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  },

  columnAction: {
    minWidth: 100,
    maxWidth: 100,
    
  },
  columnActionWide: {
    minWidth: 180,
    maxWidth: 180,
   
  },
}));

interface DraftDeductionsTableProps {
  disabled?: boolean;
  fields: DraftDeductionField[];
  handleMenuOptionClick: (option: string, index: number) => void;
  handleConfirmClick: (id: number) => void;
  deductionTotal: {
    total: number;
    liens: number;
    liensWithheld: number;
    advances: number;
    advancesWithheld: number;
    serviceProviders: number;
    serviceProvidersWithheld: number;
  };
}

const DraftDeductionsTable = ({
  // disabled,
  fields,
  handleMenuOptionClick,
  handleConfirmClick,
  deductionTotal,
}: DraftDeductionsTableProps) => {
  const isLongData = false;
  const styles = useStyles({ isLongData });

  const [liensPage, setLiensPage] = useState(0);
  const [liensRowsPerPage, setLiensRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);
  const [serviceProviderPage, setServiceProviderPage] = useState(0);
  const [serviceProviderRowsPerPage, setServiceProviderRowsPerPage] = useState(
    ROWS_PER_PAGE_OPTIONS[0],
  );

  const [expensesPage, setExpensesPage] = useState(0);
  const [expensesRowsPerPage, setExpensesRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);

  const renderTable = (title: string) => {
    const isLien = title === 'Lien';
    const isServiceProvider = title === 'Service Provider';
    const isExpense = title === 'Advance';
    const page = isLien ? liensPage : isServiceProvider ? serviceProviderPage : expensesPage;
    const rowsPerPage = isLien
      ? liensRowsPerPage
      : isServiceProvider
        ? serviceProviderRowsPerPage
        : expensesRowsPerPage;

    const setPage = isLien
      ? setLiensPage
      : isServiceProvider
        ? setServiceProviderPage
        : setExpensesPage;
    const setRowsPerPage = isLien
      ? setLiensRowsPerPage
      : isServiceProvider
        ? setServiceProviderRowsPerPage
        : setExpensesRowsPerPage;

    const dedType =
      title === 'Lien' ? 'Lien' : title === 'Service Provider' ? 'SERVICE_PROVIDER' : 'Advance';

    //  Filter deductions by type
    const filteredIndices = fields
      .map((field, idx) => ({ field, idx }))
      .filter(({ idx }) => fields?.[idx]?.type === dedType)
      .map(({ idx }) => idx);

    const paginatedIndices = filteredIndices.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );

    return (
      <Box className={styles.classes.tableWrapper}>
        <Box display="flex" alignItems="center" width="99%" justifyContent="space-between">
          <Typography className={styles.classes.tableTitle}>
            {title === 'Lien'
              ? 'Liens'
              : title === 'Service Provider'
                ? 'Service Providers'
                : 'Expenses'}
          </Typography>
          <Typography>
            {title === 'Lien'
              ? numberToUSDCurrency(deductionTotal.liens)
              : title === 'Service Provider'
                ? numberToUSDCurrency(deductionTotal.serviceProviders)
                : numberToUSDCurrency(deductionTotal.advances)}
          </Typography>
        </Box>
        <TableContainer
          className={styles.classes.tableContainer}
          sx={{ width: '99%' }}
          component={Paper}
          elevation={0}
        >
          <Table
            sx={{
              minWidth: '430px',
              borderCollapse: 'collapse',
            }}
          >
            <TableHead className={styles.classes.tableHeaderCell}>
              <TableRow>
                <TableCell key={'Name'} className={styles.classes.columnName}>
                  {isLien || isServiceProvider ? 'Name' : 'Description'}
                </TableCell>
                <TableCell key={'Amount'} className={styles.classes.columnAmount}>
                  {'Amount'}
                </TableCell>
                {!isExpense && (
                  <TableCell key={'PayAccount'} className={styles.classes.columnAccount}>
                    {'PAY Account'}
                  </TableCell>
                )}
                <TableCell key={'confirm'} className={styles.classes.columnAction}></TableCell>
                <TableCell key={'Action'} className={styles.classes.columnActionWide}>
                  {'Action'}
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody className={clsx(styles.classes.tableBodyCell)}>
              {paginatedIndices.length > 0 ? (
                paginatedIndices.map((idx) => {
                  const field = fields[idx];
                  const isRedRow =
                    (field.type === 'Lien' || field.type === 'SERVICE_PROVIDER') &&
                    field.matchingMode == null;
                  const isYellowRow =
                    (field.type === 'Lien' || field.type === 'SERVICE_PROVIDER') &&
                    field.matchingMode === 'fuzzy';
                  return (
                    <TableRow key={`${title}-${field.id}-${idx}`}>
                      {(field.type === 'Lien' || field.type === 'SERVICE_PROVIDER') &&
                        (field.matchingMode === null || field.matchingMode === 'fuzzy') ? (
                        <TableCell
                          className={clsx(
                            styles.classes.columnName,
                            isRedRow
                              ? styles.classes.redLeftBorder
                              : isYellowRow
                                ? styles.classes.yellowLeftBorder
                                : '',
                          )}
                        >
                          <>
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="flex-start"
                              gap="6px"
                            >
                              {fields[idx]?.vendorName ? (
                                <>
                                <TruncatedText text={fields[idx].vendorName} maxLength={30} />
                                {field.matchingMode === 'fuzzy' &&
                                  field.vendorSuggestions?.[0]?.accounting?.isSynced !==
                                  undefined && (
                                    <Box
                                      sx={{
                                        backgroundColor: field.vendorSuggestions[0].accounting
                                          .isSynced
                                          ? 'success.main'
                                          : 'warning.main',
                                        color: 'white',
                                        fontSize: '10px',
                                        padding: '2px 6px',
                                        borderRadius: '8px',
                                        marginLeft: '1px',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {field.vendorSuggestions[0].accounting.isSynced
                                        ? 'Synced'
                                        : 'Unsynced'}
                                    </Box>
                                  )}
                              </>
                              ) : (
                                <Box display="flex" alignItems="center" gap="6px">
                                  <WarningIcon
                                    sx={{ fontSize: '16px', color: '#FF8663 !important' }}
                                  />
                                  <Typography sx={{ color: '#FF8663 !important' }}>
                                    Vendor not found
                                  </Typography>
                                </Box>
                              )}
                            </Box>

                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="flex-start"
                              gap={1}
                              paddingTop='4px'
                            >
                              <TruncatedText
                                text={field.originalText}
                                maxLength={30}
                                sx={{ color: 'common.white' }}
                              />

                              <Box
                                sx={{
                                  backgroundColor: 'secondary.dark',
                                  color: 'white',
                                  fontSize: '10px',
                                  padding: '2px 6px',
                                  borderRadius: '8px',
                                  marginLeft: '1px',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                From Memo
                              </Box>

                            </Box>
                          </>
                        </TableCell>
                      ) : field.type === 'Lien' || field.type === 'SERVICE_PROVIDER' ? (
                        <TableCell
                          className={clsx(
                            styles.classes.columnName,
                            isRedRow ? styles.classes.redLeftBorder : '',
                          )}
                        >
                          <Box display="flex" alignItems="center" gap={1} overflow="hidden">
                            <Box display="flex" alignItems="center" gap="8px" overflow="hidden">
                              <TruncatedText text={fields[idx].vendorName} maxLength={30} />
                              {fields[idx]?.accounting?.link != null && (
                                <Tooltip
                                  title={`View Vendor in ${NETSUITE_LABEL}`}
                                  arrow
                                  componentsProps={{
                                    tooltip: {
                                      sx: (theme) => ({
                                        backgroundColor: theme.palette.common.white,
                                        color: theme.palette.primary.light,
                                        borderRadius: '5px',
                                        boxShadow: '3px',
                                        fontFamily: 'Inter',
                                        fontSize: '13px',
                                        fontWeight: 500,
                                        padding: '5px',
                                        maxWidth: '140px',
                                      }),
                                    },
                                  }}
                                >
                                  <LaunchIcon
                                    sx={{
                                      cursor: 'pointer',
                                      color: 'common.white',
                                      fontSize: '20px',
                                      flexShrink: 0,
                                    }}
                                    onClick={() => window.open(field.accounting?.link || '', '_blank')}
                                  />
                                </Tooltip>
                              )}
                            </Box>
                            {field.accounting?.isSynced !== undefined && (
                              <Box
                                sx={{
                                  backgroundColor: field.accounting.isSynced
                                    ? 'success.main'
                                    : 'warning.main',
                                  color: 'white',
                                  fontSize: '10px',
                                  padding: '2px 6px',
                                  borderRadius: '8px',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {field.accounting.isSynced ? 'Synced' : 'Unsynced'}
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                      ) : (
                        <TableCell className={styles.classes.columnName}>
                          <Box display="flex" alignItems="center" justifyContent="space-between">
                            <TruncatedText text={fields[idx].description || '-'} maxLength={30} />
                          </Box>
                        </TableCell>
                      )}

                      <TableCell className={styles.classes.columnAmount}>
                        {numberToUSDCurrency(fields[idx]?.amount) || '-'}
                      </TableCell>

                      {!isExpense && (
                        <TableCell className={styles.classes.columnAccount}>
                          {['PAY', 'Pay'].includes(field.disposition)
                            ?
                                field.vendorPaymentAccountName ||
                                (field as DraftDeductionField & { paymentAccountName?: string | null })
                                  .paymentAccountName ||
                                '-'
                            : '-'}
                        </TableCell>
                      )}

                      <TableCell className={styles.classes.columnAction}>
                        {(field.matchingMode === 'fuzzy' && field.vendorSuggestions?.[0]?.accounting && field.vendorSuggestions?.[0]?.accounting?.isSynced) && (
                          <Box>
                            <CommonButton
                              disabled={false}
                              type="infoMain"
                              buttonText="Confirm"
                              minWidth="90px"
                              maxWidth='110px'
                              onButtonClick={() => handleConfirmClick(fields[idx].id)}
                            />
                          </Box>
                        )}
                         {(field.matchingMode === 'fuzzy' && field.vendorSuggestions?.[0]?.accounting === null) && (
                          <Box>
                            <CommonButton
                              disabled={false}
                              type="infoMain"
                              buttonText="Confirm"
                              // minWidth="90px"
                              maxWidth='110px'
                              onButtonClick={() => handleConfirmClick(fields[idx].id)}
                            />
                          </Box>
                        )}
                      </TableCell>

                      <TableCell className={styles.classes.columnActionWide}>
                        <>
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="flex-start"
                            gap={4}
                          >
                            <Box>
                              <EditIcon
                                sx={{ cursor: 'pointer', color: 'common.white' }}
                                onClick={() =>
                                  handleMenuOptionClick('Edit', Number(fields[idx].id))
                                }
                                fontSize="small"
                              />
                            </Box>

                            <Box>
                              <DeleteIcon
                                sx={{ cursor: 'pointer', color: 'error.main', marginLeft: '12px' }}
                                onClick={() =>
                                  handleMenuOptionClick('Delete', Number(fields[idx].id))
                                }
                                fontSize="small"
                              />
                            </Box>
                          </Box>
                        </>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={isExpense ? 4 : 5}
                    align="center"
                    sx={{ textAlign: 'center !important' }}
                  >
                    <Typography variant="body2" color="textSecondary">
                      No{' '}
                      {(title === 'Lien'
                        ? 'Lien'
                        : title === 'Service Provider'
                          ? 'Service Provider'
                          : 'Expense'
                      ).toLowerCase()}{' '}
                      records found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePaginationLocalContainer
          count={filteredIndices.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value as string, ROWS_PER_PAGE_OPTIONS[0]));
            setPage(0);
          }}
          totalPages={Math.ceil(filteredIndices.length / rowsPerPage)}
        />
      </Box>
    );
  };

  return (
    <>
      <Accordion className={styles.classes.accordionRoot} defaultExpanded>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
          className={styles.classes.accordionSummary}
        >
          <Box display="flex" alignItems="center" width="100%" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap="8px">
              <Typography>Deductions</Typography>
              <Typography className={styles.classes.labelText}>
                Liens, Service Providers & Expenses
              </Typography>
            </Box>
            <Typography>{numberToUSDCurrency(deductionTotal.total)}</Typography>
          </Box>
        </AccordionSummary>

        <AccordionDetails className={styles.classes.accordionDetails}>
          {renderTable('Lien')}
          {renderTable('Service Provider')}
          {renderTable('Advance')}
        </AccordionDetails>
      </Accordion>
    </>
  );
};

export default DraftDeductionsTable;
